/**
 * Analytics Tracking System
 *
 * This class provides comprehensive event tracking functionality:
 * 1. Click tracking for elements with [data-track-click]
 * 2. Form submission tracking for forms with [data-track-submit]
 * 3. Special handling for capturing and including hashed emails from form responses
 *
 * The system pushes events to the Google Tag Manager dataLayer with standardized
 * formatting. It provides automatic tracking setup on initialization and handles
 * various edge cases for reliability.
 */
export default class {
  constructor() {
    // Bind methods to maintain 'this' context
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleFormResponse = this.handleFormResponse.bind(this);

    // Initialize dataLayer for GTM if not already present
    window.dataLayer = window.dataLayer || [];

    // WeakMap provides automatic garbage collection when forms are removed from DOM
    // This prevents memory leaks while storing hashed emails
    this.hashStorage = new WeakMap();
  }

  /**
   * Getter for dataLayer - provides access to GTM's dataLayer array
   */
  get dataLayer() {
    return window.dataLayer;
  }

  /**
   * Initialize analytics tracking
   * Sets up event handlers and form response interception
   */
  init() {
    this.updateHandlers();
    this.setupFormInterception();
  }

  /**
   * Build a standard tracking event object
   * Ensures consistent event formatting for GTM
   *
   * @param {Object} eventDetails - Raw event data to format
   * @returns {Object} Properly formatted event object for dataLayer
   */
  buildEvent(eventDetails) {
    const defaultEventType = 'GAEvent';
    eventDetails.eventType = eventDetails.eventType || defaultEventType;
    const { eventType, ...eventProperties } = eventDetails;
    return { event: eventType, ...eventProperties };
  }

  /**
   * Push an event to the dataLayer
   * Handles different parameter formats for flexibility
   *
   * @param {Object|string} eventType - Either event object or event type string
   * @param {Object} [properties] - Event properties if eventType is a string
   */
  trackEvent(eventType, properties) {
    let event = {};
    if (typeof properties === 'undefined') {
      // First argument is an event object
      properties = eventType;
      eventType = properties.eventType;
    }

    event = this.buildEvent({ eventType, ...properties });
    this.dataLayer.push(event);
  }

  /**
   * Set up click tracking for elements with data-track-click
   * Dynamically attaches event listeners to matching elements
   */
  updateClicks() {
    const clickElements = [...document.querySelectorAll('[data-track-click]')];
    clickElements.forEach((btn) => {
      // Remove existing listeners to prevent duplicates
      btn.removeEventListener('click', this.handleClick);
      btn.addEventListener('click', this.handleClick);
    });
  }

  /**
   * Set up form submission tracking for forms with data-track-submit
   * Dynamically attaches event listeners to matching forms
   */
  updateForms() {
    const formElements = [...document.querySelectorAll('[data-track-submit]')];
    formElements.forEach((form) => {
      // Remove existing listeners to prevent duplicates
      form.removeEventListener('submit', this.handleFormSubmit);
      form.addEventListener('submit', this.handleFormSubmit);
    });
  }

  /**
   * Update all event handlers
   * Called during initialization to set up all tracking
   */
  updateHandlers() {
    this.updateClicks();
    this.updateForms();
  }

  /**
   * Set up interception of form responses to capture hashed emails
   *
   * This is the core of the hashed email tracking system. It:
   * 1. Intercepts fetch responses that match form submission patterns
   * 2. Looks for hashedEmail in successful responses
   * 3. Identifies which form the response belongs to
   * 4. Stores the hash and triggers tracking with it included
   */
  setupFormInterception() {
    const trackingForms = document.querySelectorAll('form[data-track-submit]');
    if (trackingForms.length === 0) return;

    // Save reference to this for use in async function
    const self = this;

    // Store original fetch to restore it if needed
    const originalFetch = window.fetch;

    // Override the global fetch to intercept responses
    window.fetch = async function(...args) {
      const [, config] = args;

      // Call original fetch to maintain normal functionality
      const response = await originalFetch.apply(this, args);

      // Check if this looks like a form submission response
      // We look for POST requests with JSON content type
      if (config &&
        config.method === 'POST' &&
        config.headers &&
        (
          (config.headers.Accept && config.headers.Accept === 'application/json') ||
          (config.headers.accept && config.headers.accept === 'application/json') ||
          (config.headers['Accept'] && config.headers['Accept'] === 'application/json') ||
          (config.headers['accept'] && config.headers['accept'] === 'application/json')
        )) {
        try {
          // Clone response so we can read it without interfering with other consumers
          const clonedResponse = response.clone();
          const data = await clonedResponse.json();

          // Check for successful response with hashed email
          if (data && data.hashedEmail && data.success === true) {
            // Find most recently submitted form by checking timestamps
            const recentForm = Array.from(document.querySelectorAll('form[data-track-submit]'))
              .sort((a, b) => {
                const timeA = parseInt(a.dataset.lastSubmitted || '0', 10);
                const timeB = parseInt(b.dataset.lastSubmitted || '0', 10);
                return timeB - timeA; // Sort descending (most recent first)
              })[0];

            if (recentForm) {
              // Store hash in WeakMap using form element as key
              self.hashStorage.set(recentForm, data.hashedEmail);

              // Get previously stored tracking data
              let trackingData;
              try {
                trackingData = JSON.parse(recentForm.dataset.pendingTrack || recentForm.dataset.trackSubmit || '{}');
              } catch (e) {
                trackingData = {};
              }

              // Add hashed email to tracking data
              trackingData.hashedEmail = data.hashedEmail;
              // use later if we need it
              localStorage.setItem('user_hashed_email', data.hashedEmail);

              let userData = {};
              let hashedKeys = Object.keys(data).filter(key => key.startsWith('hashed'));

              if (hashedKeys.length > 0) {
                // Add hashed data to user_data
                hashedKeys.forEach(key => {
                  let newKey = key.replace('hashed', '');
                  newKey = newKey.replace(/([A-Z]+)/g, '_\$1').toLowerCase().replace(/(^\_|_$)/, '')
                  userData[newKey] = data[key];
                });

                trackingData.user_data = userData;
              }

              // Fire tracking event with complete data
              self.trackEvent(trackingData);

               // Fire additional event if contactEmailOptIn was checked
              if (recentForm.dataset.contactEmailOptIn === 'true') {
                self.trackEvent({
                  eventCategory: 'Lead_Generation',
                  eventAction: 'Email_Opt_In',
                  eventLabel: 'Consultation_Request',
                  eventType: 'GAEvent',
                  hashedEmail: data.hashedEmail, // Include hashed email in opt-in event too
                  user_data: trackingData.user_data,
                });
              }

              // Clean up temporary data
              delete recentForm.dataset.pendingTrack;
              delete recentForm.dataset.contactEmailOptIn;
            }
          }
        } catch (e) {
          // Silent fail for non-JSON responses or parsing errors
        }
      }

      // Return original response to maintain normal flow
      return response;
    };

    // Ensure all forms have IDs for easier tracking
    trackingForms.forEach(form => {
      if (!form.id) {
        form.id = `form-${Math.random().toString(36).substring(2, 9)}`;
      }
    });
  }

  /**
   * Handle a successful form response with hashed email
   * Creates and fires a success event with the hash included
   *
   * @param {Element} form - The form element
   * @param {string} hashedEmail - The hashed email from the response
   */
  handleFormResponse(form, hashedEmail) {
    if (!form || !form.dataset.trackSubmit) return;

    try {
      const trackData = JSON.parse(form.dataset.trackSubmit || '{}');
      const successEvent = {
        ...trackData,
        hashedEmail,
      };

      this.trackEvent(successEvent);
    } catch (e) {
      // Silent fail
    }
  }

  /**
   * Handle click events for elements with data-track-click
   * Reads tracking configuration from data-track-click attribute (JSON format)
   * Fires tracking event with element's configured data
   *
   * @param {Event} event - The click event
   */
  handleClick(event) {
    try {
      let elem = null;

      // Find the element with tracking data
      // Could be the target itself or a parent with [data-track-click]
      if (event.target.matches('[data-track-click]')) {
        elem = event.target;
      } else {
        elem = event.target.closest('[data-track-click]');
      }

      if (elem && elem.dataset.trackClick) {
        // Parse tracking data from element's data attribute
        const { eventType, ...eventParams } = this.buildEvent(JSON.parse(elem.dataset.trackClick || '{}'));
        this.trackEvent(eventType, eventParams);
      }
    } catch (e) {
      // Silent fail
    }
  }

  /**
   * Handle form submissions
   * Instead of immediately tracking, this:
   * 1. Stores data temporarily with a timestamp
   * 2. The fetch interceptor will handle actual tracking after response is received
   * 3. Includes any previously captured hash if available
   *
   * @param {Event} event - The form submission event
   */
  handleFormSubmit(event) {
    try {
      const form = event.target;
      if (!form.dataset.trackSubmit) return;

      // Parse tracking data from form
      const trackData = JSON.parse(form.dataset.trackSubmit || '{}');

      // Add timestamp to identify this as the most recently submitted form
      form.dataset.lastSubmitted = Date.now().toString();

      // Store checkbox state for successful submission tracking
      const contactEmailOptIn = form.querySelector('input[name="contactEmailOptIn"]');
      if (contactEmailOptIn && contactEmailOptIn.checked) {
        form.dataset.contactEmailOptIn = 'true';
      } else {
        delete form.dataset.contactEmailOptIn;
      }

      // If we already have a hash from previous submission, include it
      if (this.hashStorage.has(form)) {
        const hashedEmail = this.hashStorage.get(form);
        const updatedTrackData = {
          ...trackData,
          hashedEmail,
        };
        form.dataset.pendingTrack = JSON.stringify(updatedTrackData);
      } else {
        // Store original tracking data
        form.dataset.pendingTrack = form.dataset.trackSubmit;
      }

      // Note: We don't fire tracking event here
      // The fetch interceptor will handle that after response is received
    } catch (e) {
      // Silent fail
    }
  }
}
